/**
 * Floodlight vs Classic look (see utils/uiStyle.js for the data model).
 *
 *   const { isClassic, style, siteDefault, userOverride } = useUiStyle();
 *   setSiteUiStyle('classic')            // admins: everyone without their own setting
 *   setUserUiStyle(uid, 'floodlight')    // admins: one person; null = follow the site default
 *   ?look=classic in the URL             // this tab only, writes nothing (clearPreview() ends it)
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from './AuthContext';
import { useNavigation } from './NavigationContext';
import {
    UI_STYLE_DOC, UI_STYLE_DEFAULT, validUiStyle, resolveUiStyle, applyUiStyle, initialUiStyle, readUiPreview, clearUiPreview,
    nextShownStyle,
} from '../utils/uiStyle';

const UiStyleContext = createContext({
    style: UI_STYLE_DEFAULT,
    isClassic: false,
    isFloodlight: true,
    siteDefault: UI_STYLE_DEFAULT,
    userOverride: null,
    ready: false,
});

// When this browser last changed a look itself, so its own change shows at once.
let localLookChangeAt = 0;
const markLocalLookChange = () => { localLookChangeAt = Date.now(); };

/** `by` is the admin's uid: site_config is publicly readable, so never an email. */
export async function setSiteUiStyle(style, by = null) {
    const s = validUiStyle(style);
    if (!s) throw new Error(`Unknown look: ${style}`);
    markLocalLookChange();
    await setDoc(doc(db, ...UI_STYLE_DOC), { default: s, updatedAt: serverTimestamp(), ...(by ? { updatedBy: by } : {}) }, { merge: true });
}

/** style null/undefined clears the person's setting so they follow the site default. */
export async function setUserUiStyle(uid, style) {
    const s = validUiStyle(style);
    markLocalLookChange();
    await updateDoc(doc(db, 'users', uid), { uiStyle: s || deleteField(), updatedAt: serverTimestamp() });
}

export function UiStyleProvider({ children }) {
    const { user, userProfile, loading: authLoading } = useAuth();
    const { currentScreen } = useNavigation();
    // Before the site doc answers, keep painting whatever this browser showed last.
    const [siteDefault, setSiteDefault] = useState(null);
    const [ready, setReady] = useState(false);
    const [initial] = useState(initialUiStyle);
    const [preview, setPreview] = useState(readUiPreview);

    useEffect(() => {
        const unsub = onSnapshot(
            doc(db, ...UI_STYLE_DOC),
            (snap) => {
                setSiteDefault(validUiStyle((snap.data() || {}).default) || UI_STYLE_DEFAULT);
                setReady(true);
            },
            () => { setSiteDefault(UI_STYLE_DEFAULT); setReady(true); },
        );
        return () => unsub();
    }, []);

    const userOverride = validUiStyle(userProfile?.uiStyle);
    const live = ready ? resolveUiStyle(siteDefault, userOverride, preview) : (preview || userOverride || initial);

    // Hold the look a screen opened with; see nextShownStyle. Updated during render
    // so a navigation never paints one frame of the old look on the new screen.
    const profileSettled = !authLoading && (!user || !!userProfile);
    const [shown, setShown] = useState(live);
    const [shownOn, setShownOn] = useState(currentScreen);
    // Settled per account: signing in or out (often with no screen change) re-opens it
    // so the newly loaded person's own look applies at once.
    const uid = user?.uid ?? null;
    const [settledFor, setSettledFor] = useState(undefined);
    const settled = settledFor === uid;
    const navigated = shownOn !== currentScreen;
    const next = nextShownStyle({ shown, live, settled, navigated, localChangeAt: localLookChangeAt });
    if (next !== shown) setShown(next);
    if (navigated) setShownOn(currentScreen);
    if (!settled && ready && profileSettled) setSettledFor(uid);
    const style = next;

    // A tab preview is not remembered as this browser's look for the next visit.
    useEffect(() => { applyUiStyle(style, { remember: !preview }); }, [style, preview]);

    const value = useMemo(() => ({
        style,
        isClassic: style === 'classic',
        isFloodlight: style !== 'classic',
        siteDefault: siteDefault || UI_STYLE_DEFAULT,
        userOverride,
        preview,
        clearPreview: () => { markLocalLookChange(); clearUiPreview(); setPreview(null); },
        ready,
    }), [style, siteDefault, userOverride, preview, ready]);

    return <UiStyleContext.Provider value={value}>{children}</UiStyleContext.Provider>;
}

export function useUiStyle() {
    return useContext(UiStyleContext);
}

/** Route helper: the Classic component when the look is Classic, else the Floodlight one. */
export function StyledScreen({ classic: Classic, floodlight: Floodlight, ...props }) {
    const { isClassic } = useUiStyle();
    const C = isClassic ? Classic : Floodlight;
    return <C {...props} />;
}
